import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { apiGet, apiPost, adminDeleteKabileAnswer } from "../lib/api";
import { getRankIcon, isErRank } from "../lib/ranks";
import "./GlobalArena.css";

interface GlobalAnswer {
  _id: string;
  kabileId: string;
  kabileName: string;
  kabileFlagUrl?: string;
  kabileTotalKP: number;
  answerText: string;
  authorUserId: string;
  authorUsername: string;
  authorTP: number;
  authorRank: number;
  kpPoints: number;
  hasVoted: boolean;
  selectedAt?: string;
}

interface GlobalBattle {
  battleId: string;
  questionText: string;
  createdAt: string;
  answers: GlobalAnswer[];
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

export default function GlobalArena() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [battles, setBattles] = useState<GlobalBattle[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasMore: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedBattles, setExpandedBattles] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadGlobalAnswers(currentPage);
  }, [currentPage]);

  const loadGlobalAnswers = async (page: number) => {
    try {
      setLoading(true);
      const res = await apiGet<{ 
        ok: true; 
        battles: GlobalBattle[];
        pagination: Pagination;
      }>(`/api/kabile/daily-battle/global?page=${page}&limit=20`);
      
      setBattles(res.battles);
      setPagination(res.pagination);
    } catch (error: any) {
      console.error("Failed to load global answers:", error);
      alert(error.message || "Global arena yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const handleKPVote = async (battleId: string, answerId: string) => {
    try {
      const res = await apiPost<{ ok: true; kpPoints: number }>(
        `/api/kabile/daily-battle/global/${battleId}/${answerId}/kp`,
        {}
      );

      // Update local state
      setBattles(prev => prev.map(battle => {
        if (battle.battleId === battleId) {
          return {
            ...battle,
            answers: battle.answers.map(a => {
              if (a._id === answerId) {
                return {
                  ...a,
                  kpPoints: res.kpPoints,
                  hasVoted: !a.hasVoted,
                };
              }
              return a;
            })
          };
        }
        return battle;
      }));
    } catch (error: any) {
      console.error("Failed to vote:", error);
      alert(error.message || "KP verilirken hata oluştu");
    }
  };

  // Helper function to check if battle is from today
  const isToday = (date: Date): boolean => {
    const today = new Date();
    const battleDate = new Date(date);
    return (
      battleDate.getDate() === today.getDate() &&
      battleDate.getMonth() === today.getMonth() &&
      battleDate.getFullYear() === today.getFullYear()
    );
  };

  const handleAdminDeleteAnswer = async (kabileId: string, authorUserId: string, kabileName: string) => {
    if (!confirm(`⚠️ ADMIN YETKİSİ\n\n${kabileName} kabilesinin cevabını silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz!`)) {
      return;
    }

    try {
      await adminDeleteKabileAnswer(kabileId, authorUserId);
      alert("Cevap admin tarafından silindi");
      // Reload data
      await loadGlobalAnswers(currentPage);
    } catch (error: any) {
      console.error("Failed to delete answer:", error);
      alert(error.message || "Cevap silinirken hata oluştu");
    }
  };

  // Get battle title (today's question or date)
  const getBattleTitle = (battle: GlobalBattle): string => {
    const dateStr = new Date(battle.createdAt).toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    
    if (isToday(new Date(battle.createdAt))) {
      return "🔥 Bugünün Sorusu";
    }
    return `📅 ${dateStr} - Günün Sorusu`;
  };

  // Toggle battle expansion to show more/less answers
  const toggleBattleExpand = (battleId: string) => {
    setExpandedBattles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(battleId)) {
        newSet.delete(battleId);
      } else {
        newSet.add(battleId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="global-arena-container">
        <div className="arena-loading">
          <div className="loading-spinner"></div>
          <p>Global Arena yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="global-arena-container">
      <div className="global-arena-content">
        {/* Header */}
        <header className="arena-header">
          <button onClick={() => navigate("/savkabile")} className="back-btn">
            ← Geri
          </button>
          <h1 className="arena-title">
            🌍 <span className="title-glow">Global Arena Arşivi</span>
          </h1>
          <p className="arena-subtitle">Kabilelerin Geçmiş Fikir Savaşları</p>
        </header>

        {/* Info */}
        <div className="arena-info">
          <p>📚 Bugüne kadar sorulan tüm soruların kabile cevapları</p>
          <p>❤️ Beğendiğin cevaplara <strong>KP (Kabile Puanı)</strong> verebilirsin</p>
          <p>🏆 En çok KP alan kabileler sıralamada yükselir</p>
        </div>

        {/* Battles List */}
        {battles.length === 0 ? (
          <div className="arena-empty">
            <div className="empty-icon">📭</div>
            <h3>Henüz Arşivlenmiş Savaş Yok</h3>
            <p>Kabile liderleri cevap seçmeye başladığında burada görünecek.</p>
          </div>
        ) : (
          <>
            {battles.map((battle) => {
              const isExpanded = expandedBattles.has(battle.battleId);
              const INITIAL_SHOW_COUNT = 3;
              const visibleAnswers = isExpanded ? battle.answers : battle.answers.slice(0, INITIAL_SHOW_COUNT);
              const remainingCount = battle.answers.length - INITIAL_SHOW_COUNT;

              return (
              <div key={battle.battleId} className="battle-section">
                {/* Question with Dynamic Title */}
                <div className="arena-question-card">
                  <div className="battle-title">{getBattleTitle(battle)}</div>
                  <h2 className="question-text">{battle.questionText}</h2>
                </div>

                {/* Answers - Show first 3 or all if expanded */}
                <div className="arena-answers-list">
                  {visibleAnswers.map((answer, index) => (
                    <div 
                      key={answer._id} 
                      className={`arena-answer-card rank-${index + 1}`}
                    >
                      {/* Kabile Info */}
                      <div className="answer-kabile-section">
                        <div className="kabile-rank-number">#{index + 1}</div>
                        <div 
                          className="kabile-info-compact"
                          onClick={() => navigate(`/savkabile/${answer.kabileId}`)}
                        >
                          {answer.kabileFlagUrl ? (
                            <img 
                              src={answer.kabileFlagUrl} 
                              alt={answer.kabileName} 
                              className="kabile-flag-tiny"
                            />
                          ) : (
                            <div className="kabile-flag-placeholder-tiny">🏛️</div>
                          )}
                          <div className="kabile-details-compact">
                            <h4 className="kabile-name-compact">{answer.kabileName}</h4>
                            <span className="kabile-total-kp">
                              💎 {answer.kabileTotalKP} KP
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Author Info */}
                      <div className="answer-author-section">
                        <span className="author-label">Cevabı yazan:</span>
                        <span 
                          className="author-name clickable"
                          onClick={() => navigate(`/u/${answer.authorUsername}`)}
                        >
                          {answer.authorUsername}
                        </span>
                        <div className="author-stats">
                          <span>⚔️ {answer.authorTP} TP</span>
                          {isErRank(answer.authorRank) ? (
                            <span style={{ fontSize: 20 }}>🟢</span>
                          ) : (
                            <img 
                              src={getRankIcon(answer.authorRank)} 
                              alt="rank" 
                              className="author-rank-icon-global"
                            />
                          )}
                        </div>
                      </div>

                      {/* Answer Text */}
                      <div className="answer-text-section">
                        <p className="answer-text">{answer.answerText}</p>
                      </div>

                      {/* Action Buttons - Admin left, KP right */}
                      <div className="answer-kp-section">
                        {/* Admin Delete Button - Left */}
                        {user?.isAdmin && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAdminDeleteAnswer(answer.kabileId, answer.authorUserId, answer.kabileName);
                            }}
                            className="admin-delete-answer-btn"
                            title="Admin: Cevabı Sil"
                          >
                            🗑️
                          </button>
                        )}

                        {/* KP Vote Button - Right */}
                        <button
                          onClick={() => handleKPVote(battle.battleId, answer._id)}
                          className={`kp-vote-btn ${answer.hasVoted ? "voted" : ""}`}
                        >
                          � <span className="kp-count">{answer.kpPoints} KP</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Show More/Less Button */}
                {battle.answers.length > INITIAL_SHOW_COUNT && (
                  <button 
                    onClick={() => toggleBattleExpand(battle.battleId)}
                    className="show-more-answers-btn"
                  >
                    {isExpanded 
                      ? '▲ Daha Az Göster' 
                      : `▼ ${remainingCount} Cevap Daha Göster`
                    }
                  </button>
                )}
              </div>
              );
            })}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="pagination-controls">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  ← Önceki
                </button>
                <span className="pagination-info">
                  Sayfa {pagination.page} / {pagination.totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={!pagination.hasMore}
                  className="pagination-btn"
                >
                  Sonraki →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
